import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CircleNotch, X, Tag } from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";
import { api } from "./api";
import { Mascot } from "@/components/styleguide/Mascot";
import { ChildAvatar } from "@/experiences/select-child/ChildAvatar";

/**
 * TrialEndedModal
 *
 * Shown in two situations:
 *  1. User clicks "Subscribe" while still trialing → canDismiss=true (X + "Not now")
 *  2. Trial has expired (auto-shown)            → canDismiss=false ("Not now — sign out")
 *
 * Props:
 *  canDismiss  boolean  — whether the user can close without subscribing
 *  onDismiss   fn       — called when user clicks X or "Not now"
 */
export default function TrialEndedModal({ canDismiss = true, onDismiss }) {
  const { logout, user } = useAuth();

  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [upgrading,    setUpgrading]    = useState(false);
  const [children,     setChildren]     = useState(null);   // null = loading
  const [showPromo,    setShowPromo]    = useState(false);
  const [promo,        setPromo]        = useState("");

  /* ── Fetch children list + word counts ────────────────────────────── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stats = await api("/parent/stats");
        const enriched = await Promise.all(
          stats.map(async ({ child, streak = null }) => {
            try {
              const [ins, plans] = await Promise.all([
                api(`/children/${child.child_id}/words/insights`),
                api(`/children/${child.child_id}/plans`).catch(() => []),
              ]);
              const confident  = ins?.counts?.confident  ?? 0;
              const struggling = ins?.counts?.struggling ?? 0;
              const learning   = ins?.counts?.learning   ?? 0;
              const seen = confident + struggling + learning;
              const quiz_count = (plans || []).reduce(
                (s, p) => s + (p.stats?.quiz_count ?? 0), 0
              );
              return { ...child, seen, quiz_count, streak };
            } catch {
              return { ...child, seen: 0, quiz_count: 0, streak };
            }
          })
        );
        if (alive) setChildren(enriched);
      } catch {
        if (alive) setChildren([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* ── Checkout ─────────────────────────────────────────────────────── */
  async function handleCheckout() {
    setUpgrading(true);
    try {
      const body = { plan: selectedPlan };
      if (promo.trim()) body.promo = promo.trim().toUpperCase();
      const { url } = await api("/billing/checkout", { method: "POST", body });
      window.location.href = url;
    } catch {
      setUpgrading(false);
    }
  }

  /* ── Derived display values ───────────────────────────────────────── */
  const totalWords = children?.reduce((s, c) => s + c.seen, 0) ?? 0;

  const expired = user?.subscription_status === "expired";
  const badgeText = expired
    ? "Your free week has ended"
    : user?.days_left === 0
      ? "Today is your last day"
      : user?.days_left != null
        ? `${user.days_left} day${user.days_left !== 1 ? "s" : ""} left on your free trial`
        : "Your free trial";

  const firstNames = children?.map(c => c.first_name) ?? [];
  const headline =
    firstNames.length === 0 ? "Keep learning" :
    firstNames.length === 1 ? `Keep ${firstNames[0]} growing` :
    `Keep ${firstNames.slice(0, -1).join(", ")} & ${firstNames.at(-1)} growing`;

  const paymentNote =
    selectedPlan === "annual"
      ? "You pay £60.00 today, then £60.00 a year. Cancel anytime."
      : "You pay £6.49 today, then £6.49 a month. Cancel anytime.";

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        onClick={() => canDismiss && onDismiss?.()}
        className={`absolute inset-0 bg-foreground/30 backdrop-blur-[2px] ${canDismiss ? "cursor-pointer" : "cursor-default"}`}
      />

      <motion.div
        initial={{ y: 48, opacity: 0 }}
        animate={{ y: 0,  opacity: 1 }}
        exit={{   y: 24,  opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="relative w-full max-w-md overflow-y-auto rounded-[2rem] bg-card shadow-soft-lg"
        style={{ maxHeight: "90svh" }}
      >
        {/* Scrollable inner */}
        <div className="p-6">

          {/* Dismiss X — only when trial still active */}
          {canDismiss && (
            <button
              onClick={onDismiss}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-muted transition hover:bg-border"
            >
              <X size={16} weight="bold" />
            </button>
          )}

          {/* ── Header ── */}
          <div className="flex flex-col items-center text-center">
            <Mascot size={44} mood="happy" animated={false} />

            <span className="mt-3 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-extrabold text-primary-strong">
              + {badgeText}
            </span>

            <h2 className="mt-3 font-display text-2xl font-black leading-tight text-foreground">
              {headline}
            </h2>

            {children !== null && totalWords > 0 && (
              <p className="mt-2 max-w-xs text-sm font-semibold text-muted-foreground">
                {children.length === 1 ? (
                  <>{firstNames[0]} has seen{" "}
                    <span className="font-extrabold text-foreground">{totalWords} words</span>{" "}
                    so far. Pick a plan to carry on where they left off.</>
                ) : (
                  <>Together they've seen{" "}
                    <span className="font-extrabold text-foreground">{totalWords} words</span>{" "}
                    so far. Pick a plan to carry on where they left off.</>
                )}
              </p>
            )}
            {children !== null && totalWords === 0 && (
              <p className="mt-2 max-w-xs text-sm font-semibold text-muted-foreground">
                Pick a plan to keep their vocabulary growing.
              </p>
            )}
          </div>

          {/* ── Child mini-cards ── */}
          {children === null ? (
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="h-20 animate-pulse rounded-2xl bg-muted" />
              <div className="h-20 animate-pulse rounded-2xl bg-muted" />
            </div>
          ) : children.length > 0 && (
            <div className={`mt-5 ${children.length === 1 ? "flex justify-center" : "grid grid-cols-2 gap-3"}`}>
              {children.map(c => (
                <div key={c.child_id} className={`rounded-2xl bg-muted/50 p-3 ${children.length === 1 ? "w-1/2" : ""}`}>
                  <div className="flex items-center gap-2">
                    <ChildAvatar variant={c.avatar} className="h-8 w-8 shrink-0" />
                    <p className="truncate font-display text-sm font-black text-foreground">
                      {c.first_name}
                    </p>
                  </div>
                  <p className="mt-1.5 text-xs font-semibold text-muted-foreground">
                    Year {c.school_year}
                    {c.streak > 0 && <> · 🔥 {c.streak}-day streak</>}
                    {c.quiz_count > 0 && (
                      <> · {c.quiz_count} quiz{c.quiz_count !== 1 ? "zes" : ""}</>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* ── Plan picker ── */}
          <div className="mt-5">
            <p className="mb-3 text-sm font-extrabold text-foreground">
              Pick a plan — covers {children?.length === 1 ? "your child" : "both children"}
            </p>

            {/* Annual */}
            <label
              className={`relative mt-1 flex cursor-pointer items-center gap-3 rounded-2xl border-2 p-4 transition ${
                selectedPlan === "annual"
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <input
                type="radio" name="plan" value="annual"
                checked={selectedPlan === "annual"}
                onChange={() => setSelectedPlan("annual")}
                className="sr-only"
              />
              {/* Radio dot */}
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                selectedPlan === "annual" ? "border-primary" : "border-muted-foreground/40"
              }`}>
                {selectedPlan === "annual" && (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-black text-foreground">Annual</p>
                <p className="text-xs font-semibold text-muted-foreground">
                  £60.00 billed once a year{" "}
                  <span className="font-extrabold text-primary-strong">Nearly 3 months free vs monthly</span>
                </p>
              </div>
              <p className="shrink-0 font-display font-black text-foreground">
                £5<span className="text-sm font-extrabold">.00/mo</span>
              </p>
              {/* Best value badge */}
              <span className="absolute -top-3 right-4 rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-extrabold text-white shadow-sm">
                Best value · Save 23%
              </span>
            </label>

            {/* Monthly */}
            <label
              className={`mt-2 flex cursor-pointer items-center gap-3 rounded-2xl border-2 p-4 transition ${
                selectedPlan === "monthly"
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <input
                type="radio" name="plan" value="monthly"
                checked={selectedPlan === "monthly"}
                onChange={() => setSelectedPlan("monthly")}
                className="sr-only"
              />
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                selectedPlan === "monthly" ? "border-primary" : "border-muted-foreground/40"
              }`}>
                {selectedPlan === "monthly" && (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-black text-foreground">Monthly</p>
                <p className="text-xs font-semibold text-muted-foreground">
                  Billed monthly · cancel anytime
                </p>
              </div>
              <p className="shrink-0 font-display font-black text-foreground">
                £6<span className="text-sm font-extrabold">.49/mo</span>
              </p>
            </label>
          </div>

          {/* ── Shared plan features ── */}
          <div className="mt-3 rounded-xl bg-muted/50 px-4 py-3 flex flex-col gap-1.5">
            <p className="text-xs font-extrabold text-foreground">Both plans include:</p>
            <p className="text-xs font-semibold text-muted-foreground leading-snug">
              ✓{" "}
              <span className="font-extrabold text-foreground">20 new word requests</span>
              {" "}per month — suggest words for your child's plan
            </p>
            <p className="text-xs font-semibold text-muted-foreground leading-snug">
              ✓ Unlimited vocabulary, quizzes &amp; 11+ prep for all children
            </p>
          </div>

          {/* ── Promo code ── */}
          <div className="mt-3">
            <button
              onClick={() => setShowPromo(s => !s)}
              className="flex items-center gap-1 text-sm font-extrabold text-primary-strong underline underline-offset-2 hover:no-underline"
            >
              <Tag size={13} weight="bold" />
              Have a code?
            </button>
            <AnimatePresence>
              {showPromo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <input
                    type="text"
                    value={promo}
                    onChange={e => setPromo(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    className="mt-2 w-full rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-sm font-extrabold uppercase tracking-wider text-foreground placeholder:font-semibold placeholder:normal-case placeholder:tracking-normal placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── CTA ── */}
          <button
            onClick={handleCheckout}
            disabled={upgrading}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-4 font-display text-base font-extrabold text-white shadow-sm transition hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60"
          >
            {upgrading
              ? <><CircleNotch size={18} className="animate-spin" /> Redirecting…</>
              : <>Continue to secure checkout <ArrowRight size={16} weight="bold" /></>
            }
          </button>

          {/* ── Footer notes ── */}
          <div className="mt-3 text-center">
            <p className="text-xs font-semibold text-muted-foreground">{paymentNote}</p>
            <p className="mt-1 text-[11px] text-muted-foreground/70">
              🔒 Secure checkout by Stripe · Cancel anytime
            </p>

            {/* "Not now" — signs out if expired, dismisses if still in trial */}
            {canDismiss ? (
              <button
                onClick={onDismiss}
                className="mt-2 text-xs font-extrabold text-muted-foreground underline underline-offset-2 hover:text-foreground hover:no-underline"
              >
                Not now
              </button>
            ) : (
              <button
                onClick={() => logout()}
                className="mt-2 text-xs font-extrabold text-muted-foreground underline underline-offset-2 hover:text-foreground hover:no-underline"
              >
                Not now — sign out
              </button>
            )}
          </div>

        </div>
      </motion.div>
    </div>
  );
}